/**
 * Which quotation lines the editor shows: a search, then a page of 50.
 *
 * Akhil, 2026-10-28: quotations routinely run past 300 lines, and finding one meant scrolling.
 * The search matches the item name, the description written on the line, the code, and the
 * customer's own line number — a number typed exactly puts THAT line first ("8" is line 8 before
 * it is #87 or a code with an 8 in it), and "#88" means line 88 and nothing else.
 *
 * Only what is SHOWN is narrowed. Subtotals and the grand total stay over the whole quotation —
 * a search must never make a quotation look cheaper than it is — and the client view and what is
 * sent are untouched.
 */

import { inSerialOrder } from './lineOrder'

export const QUOTE_PAGE_SIZE = 50

/** The fields the view reads — a `QuotationLine` satisfies it. */
export interface ViewLine {
  line_id: string
  cust_sno: string
  item_code: string
  item_name?: string
  description?: string
  tag: 'provision' | 'rfq'
}

export interface QuoteLineView<T> {
  /** This page's lines, in display order. */
  lines: T[]
  /** The page shown, clamped to what exists (1-based). */
  page: number
  pageCount: number
  /** Lines matching the search — all lines when it is blank. */
  total: number
  /** 1-based position of this page's first and last line among the matches; 0 when none. */
  from: number
  to: number
}

const GROUP_ORDER = ['provision', 'rfq'] as const

function ordered<T extends ViewLine>(lines: readonly T[], grouped: boolean): T[] {
  if (!grouped) return inSerialOrder(lines)
  return GROUP_ORDER.flatMap((tag) => inSerialOrder(lines.filter((l) => l.tag === tag)))
}

function search<T extends ViewLine>(lines: readonly T[], raw: string): T[] {
  const query = raw.trim().toLowerCase()
  if (query === '') return [...lines]

  // "#88" is a line number and only that.
  const hashed = /^#\s*(\S+)$/.exec(query)
  if (hashed) return lines.filter((l) => l.cust_sno.trim().toLowerCase() === hashed[1])

  const matches = lines.filter((l) =>
    [l.cust_sno, l.item_code, l.item_name ?? '', l.description ?? ''].some((field) => field.toLowerCase().includes(query)),
  )
  const exact = matches.filter((l) => l.cust_sno.trim().toLowerCase() === query)
  return [...exact, ...matches.filter((l) => !exact.includes(l))]
}

/** Every line the search matches, in display order — the pages are cut from this. */
export function matchingLines<T extends ViewLine>(lines: readonly T[], query: string, grouped: boolean): T[] {
  return search(ordered(lines, grouped), query)
}

export function quoteLineView<T extends ViewLine>(
  lines: readonly T[],
  options: { query: string; page: number; grouped: boolean },
): QuoteLineView<T> {
  const found = matchingLines(lines, options.query, options.grouped)
  const pageCount = Math.max(1, Math.ceil(found.length / QUOTE_PAGE_SIZE))
  const page = Math.min(Math.max(1, Math.floor(options.page) || 1), pageCount)
  const start = (page - 1) * QUOTE_PAGE_SIZE
  const shown = found.slice(start, start + QUOTE_PAGE_SIZE)
  return {
    lines: shown,
    page,
    pageCount,
    total: found.length,
    from: shown.length ? start + 1 : 0,
    to: start + shown.length,
  }
}
